// The only place this module talks to the Precision Admin API (Cloud Run, Ketosource/ketosource-app-admin-panel
// api/). The Pages Function proxy (functions/precision_admin*/) strips the portal prefix and forwards the Access
// JWT; the API verifies it, asks the portal for module:precision_admin, and answers the Worker's JSON route for
// route. `precision_admin` reads the live app, `precision_admin_dev` the DEV one (restored to Tech 31 Oct 2026).

export type FreshnessMeta = {
  data_as_of: string | null;
  source: string | null;
  expected_max_age_hours: number | null;
};

export type Me = { email: string; writes: boolean; env: string; change_program: boolean };

/** Plain words for the server's refusal keys (the Worker's ERRS, unchanged). */
export const ERRS: Record<string, string> = {
  reason_required: "Write a reason first.",
  bad_dose: "That dose is not allowed for this product: use whole product steps, not above the target.",
  bad_rung: "That step is not offered on this customer's plan.",
  unknown_case_type: "Pick a case type.",
  unknown_case_id: "That case no longer exists. Next step: set Answers to “Nothing open”, then Save: a new case ID is made on save.",
  already_paused: "The plan is paused. Use Resume.",
  not_paused: "The plan is not paused.",
  no_active_run: "This customer has no plan with a check-in yet.",
  unknown_alert: "That alert belongs to another customer.",
  unknown_handoff: "That hand-off belongs to another customer.",
  writes_off: "Changes are switched off until the app follows them.",
  note_too_long: "Keep the note under 1,000 characters.",
  staff_email_required: "You are not signed in.",
  forbidden: "You do not have edit access.",
  nothing_changed: "Change at least one thing.",
  bad_product: "Pick a product from the list.",
  bad_goal: "Pick a goal from the list.",
  instructions_too_long: "Keep the instructions under 500 characters.",
  goal_change_alone: "Change the goal on its own: the new goal sets its own start dose and step.",
  no_start_answers: "This customer has no start answers to build the new goal from.",
  // Draft create-programme words (DEC-1354 PA-START-COPY-1c); remove this note when CREATE_COPY_DRAFT is signed off.
  has_active_run: "She already has a programme running. Close this and use Change programme.",
  no_rules_for_goal: "This goal has no dosing rules yet, so a programme can’t start on it. Pick another goal.",
  goal_required: "Pick her goal.",
  product_required: "Pick the product she has.",
  no_dose: "Pick a start step.",
  busy: "Her programme was started a moment ago. Close this and open her page again.",
  no_start: "Her start answers could not build a plan for this goal and product. Pick another, or ask her to check her start answers in the app.",
  missing_answer: "The customer's start answers are incomplete for this goal.",
  unknown_version: "Pick a version from the list.",
  bad_release: "Pick a protocol version from the list.",
  release_change_alone: "Change the protocol version on its own (instructions may go with it).",
  other_run: "That version belongs to an earlier goal. Change the goal instead.",
  change_program_off: "This change is not switched on yet.",
  "bad filter": "Check the filters: real dates and a full email address.",
  bad_weekly_step: "The weekly increase must be 0 or more ml.",
  weekly_step_not_multiple: "The weekly increase must fit this product's measuring step.",
  weekly_step_above_max: "The weekly increase cannot be more than the target dose.",
  weekly_step_conflict: "Set a weekly increase or go back to the default, not both.",
  pause_change_alone: "Pause the plan on its own.",
  bad_enabled: "Choose on or off.",
  bad_referral_day: "Day must be 1–60.",
  bad_cut_off: "Cut-off must be 1–10.",
  bad_step: "Pick a step from the list.",
  step_with_product_change: "The new product has its own steps. Set the dose instead of a step.",
  step_dose_mismatch: "That dose is not the dose of the step you picked. Pick the step again or clear it.",
  above_target: "Above her target. Enter the target or less.",
  reason_too_long: "Keep the reason under 2,000 characters.",
  too_large: "That is too much to send at once. Shorten the text and try again.",
  bad_body: "The change could not be read. Reload the page and try again.",
  complete: "This customer's check-ins are complete: there is nothing to reset.",
  rule_incomplete: "Fill in on/off, the day and the cut-off.",
  reset_note_too_long: "Keep the note under 980 characters.",
  unauthorized: "You are not signed in.",
  not_configured: "This panel is not connected to its data yet.",
  bad_origin: "Save from the portal page.",
};

export class AdminError extends Error {
  constructor(
    public status: number,
    public key: string | null,
    message: string,
    /** The refusal's JSON body: `max_ml`, `goal_key`, `field` (v3.1 field-level errors). */
    public body: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

async function fail(r: Response): Promise<never> {
  const j = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  const key = typeof j.error === "string" ? j.error : null;
  // 759: a weekly rise off the product's step or above the target comes back with the value to use.
  const hint =
    (typeof j.round_step_ml === "number" ? ` Nearest that fits: ${j.round_step_ml} ml a week.` : "") +
    (typeof j.max_ml === "number" ? ` Most allowed: ${j.max_ml} ml a week.` : "");
  throw new AdminError(r.status, key, ((key && ERRS[key]) || `HTTP ${r.status}`) + hint, j);
}

export type Api = {
  base: string;
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body: unknown) => Promise<T>;
  url: (path: string) => string;
};

export function makeApi(base: string): Api {
  const url = (path: string) => `${base}/api/${path}`;
  return {
    base,
    url,
    async get<T>(path: string) {
      const r = await fetch(url(path), { credentials: "include", headers: { Accept: "application/json" } });
      if (!r.ok) await fail(r);
      return (await r.json()) as T;
    },
    async post<T>(path: string, body: unknown) {
      const r = await fetch(url(path), {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) await fail(r);
      return (await r.json()) as T;
    },
  };
}

/** The data is read live from the app's database on every request, so its date is the moment of the read. */
export function liveMeta(readAt: number | undefined, env: string | undefined): FreshnessMeta | null {
  if (!readAt) return null;
  return {
    data_as_of: new Date(readAt).toISOString(),
    source: env === "prod" ? "Precision app database (live)" : "Precision app database (DEV)",
    expected_max_age_hours: 1,
  };
}

// Shapes the views read. Loose on purpose: the API passes the database functions' JSON through unchanged.
export type Row = Record<string, any>;
